import React, { useCallback } from "react";
import { LinkedJournalEntry } from "@common/types/financialDocument";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { useEntitiesById } from "@hooks/useEntities";
import { useOverlays } from "contexts/OverlayContext";
import { useDocumentDetails } from "../../useDocumentDetails";
import { FinancialDocumentLinkRow } from "./FinancialDocumentLinkRow";

interface Props {
    linkedJournalEntry: LinkedJournalEntry;
}

export const FinancialJournalEntryLink: React.FC<Props> = ({
    linkedJournalEntry,
}) => {
    const entities = useEntitiesById();
    const entity = entities.get(linkedJournalEntry.entityId);
    const { openOverlay } = useOverlays();
    const { shownDocumentId, closeDetails } = useDocumentDetails();

    const handleClick = useCallback(() => {
        if (!entity) {
            return;
        }

        if (shownDocumentId) {
            closeDetails();
        }

        openOverlay("journalEntryDrawer", {
            entity,
            ledgerBasis: linkedJournalEntry.ledgerBasis,
            ledgerId: linkedJournalEntry.ledgerId,
            journalEntryId: linkedJournalEntry.referenceNumber,
        });
    }, [
        entity,
        linkedJournalEntry,
        openOverlay,
        shownDocumentId,
        closeDetails,
    ]);

    const memo =
        linkedJournalEntry.memo ??
        (linkedJournalEntry.source === "opening_balance"
            ? "Opening balance"
            : null);

    const referenceNumber = getShortJournalEntryReferenceNumber(
        linkedJournalEntry.referenceNumber,
    );
    const description = memo ? `${referenceNumber} · ${memo}` : referenceNumber;

    return (
        <FinancialDocumentLinkRow
            dateIso={linkedJournalEntry.date}
            description={description}
            testId="financial-document-journal-entry-item"
            dateTestId="financial-document-journal-entry-item-date"
            descriptionTestId="financial-document-journal-entry-item-reference"
            view={
                entity
                    ? {
                          onClick: handleClick,
                          tooltip: "View journal entry",
                          testId: "financial-document-journal-entry-view-button",
                      }
                    : undefined
            }
        />
    );
};
